import type { AtlasSourceDescriptor, WorldAtlasAssetProvider } from './assetProvider';
import type {
  AtlasAnimationGroup,
  AtlasAssetKind,
  AtlasFrame,
  AtlasManifest,
  AtlasPage,
} from './atlasTypes';
import { buildRuntimeAtlasManifest } from './runtimeAtlasBuilder';

interface RuntimeAtlasAssetProviderOptions {
  readonly sourceFactory: () => readonly AtlasSourceDescriptor[];
  readonly pageSize?: number;
  readonly padding?: number;
}

export class RuntimeAtlasAssetProvider implements WorldAtlasAssetProvider {
  private readonly sourceFactory: () => readonly AtlasSourceDescriptor[];
  private readonly pageSize: number | undefined;
  private readonly padding: number | undefined;
  private manifest: AtlasManifest | null = null;
  private pendingManifest: Promise<AtlasManifest> | null = null;
  private readonly pageById = new Map<string, AtlasPage>();
  private readonly frameById = new Map<string, AtlasFrame>();
  private readonly animationGroupById = new Map<string, AtlasAnimationGroup>();
  private readonly terrainFrameBySourceId = new Map<string, AtlasFrame>();
  private readonly objectFrameBySourceId = new Map<string, AtlasFrame>();

  constructor(options: RuntimeAtlasAssetProviderOptions) {
    this.sourceFactory = options.sourceFactory;
    this.pageSize = options.pageSize;
    this.padding = options.padding;
  }

  public async ensureReady(): Promise<void> {
    if (this.manifest) {
      return;
    }
    if (!this.pendingManifest) {
      console.info('[World2DAtlas] Building runtime atlas manifest.');
      this.pendingManifest = buildRuntimeAtlasManifest(this.sourceFactory(), {
        pageSize: this.pageSize,
        padding: this.padding,
      }).then((manifest) => {
        this.installManifest(manifest);
        console.info(
          '[World2DAtlas] Runtime atlas manifest ready.',
          {
            pageCount: manifest.pages.length,
            frameCount: manifest.frames.length,
            animatedFrameCount: manifest.frames.filter((frame) => frame.animated).length,
            animationGroupCount: manifest.animationGroups.length,
          },
        );
        return manifest;
      }).finally(() => {
        this.pendingManifest = null;
      });
    }
    await this.pendingManifest;
  }

  public isReady(): boolean {
    return this.manifest !== null;
  }

  public getManifest(): AtlasManifest | null {
    return this.manifest;
  }

  public getPage(pageId: string): AtlasPage | null {
    return this.pageById.get(pageId) ?? null;
  }

  public getFrame(frameId: string): AtlasFrame | null {
    return this.frameById.get(frameId) ?? null;
  }

  public getTerrainFrame(terrainId: string): AtlasFrame | null {
    return this.terrainFrameBySourceId.get(terrainId) ?? null;
  }

  public getObjectFrame(assetId: string): AtlasFrame | null {
    return this.objectFrameBySourceId.get(assetId) ?? null;
  }

  public getAnimationGroup(groupId: string): AtlasAnimationGroup | null {
    return this.animationGroupById.get(groupId) ?? null;
  }

  public listFrames(kind?: AtlasAssetKind): readonly AtlasFrame[] {
    const frames = [...this.frameById.values()];
    return typeof kind === 'string' ? frames.filter((frame) => frame.kind === kind) : frames;
  }

  private installManifest(manifest: AtlasManifest): void {
    this.manifest = manifest;
    this.pageById.clear();
    this.frameById.clear();
    this.animationGroupById.clear();
    this.terrainFrameBySourceId.clear();
    this.objectFrameBySourceId.clear();

    for (const page of manifest.pages) {
      this.pageById.set(page.id, page);
    }

    for (const frame of manifest.frames) {
      this.frameById.set(frame.id, frame);
      if (frame.kind === 'terrain') {
        this.terrainFrameBySourceId.set(frame.sourceId, frame);
      } else if (frame.kind === 'object') {
        this.objectFrameBySourceId.set(frame.sourceId, frame);
      }
    }

    for (const group of manifest.animationGroups) {
      this.animationGroupById.set(group.id, group);
    }
  }
}
